import React, { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Video } from "lucide-react";

interface GalleryImageItemProps {
  imageUrl: string;
  imageId: string;
  index: number;
  onToggle: (imageId: string) => void;
  onImageLoad: (index: number) => void;
  user?: User | null;
  onLoginRequired?: (redirectPath: string, redirectState?: Record<string, unknown>) => void;
}

const GalleryImageItem = memo(({
  imageUrl,
  imageId,
  index,
  onToggle,
  onImageLoad,
  user,
  onLoginRequired,
}: GalleryImageItemProps) => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (window.innerWidth < 768) {
      onToggle(imageId);
    }
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
    onImageLoad(index);
  };

  const handleCreateVideo = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user is logged in - if not, show login prompt
    if (!user) {
      if (onLoginRequired) {
        onLoginRequired("/video-generator", { imageUrl });
      }
      // Don't navigate if user is not logged in
      return;
    }
    
    navigate("/video-generator", {
      state: {
        imageUrl,
      },
    });
  };

  return (
    <div
      className="gallery-item relative group cursor-pointer overflow-hidden rounded-lg md:hover-glow-gold border-premium"
      style={{
        aspectRatio: "9/16",
        willChange: "opacity",
        transform: "translateZ(0)",
      }}
      data-gallery-image
      data-image-id={imageId}
      onClick={handleClick}
    >
      {!isLoaded && <Skeleton className="w-full h-full animate-shimmer" />}
      <img
        src={imageUrl}
        alt={`Gallery image ${index + 1}`}
        className={`w-full h-full object-cover transition-all duration-500 md:group-hover:scale-110 ${isLoaded ? "opacity-100" : "opacity-0"}`}
        loading="lazy"
        onLoad={handleImageLoad}
      />
      <div
        className="gallery-overlay absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 transition-opacity duration-300 opacity-0 md:group-hover:opacity-100"
      />
      <div
        className="gallery-ring absolute inset-0 ring-2 transition-all duration-300 ring-transparent md:group-hover:ring-gold"
      />

      {/* Create Video Button */}
      <Button
        onClick={handleCreateVideo}
        className="gallery-button absolute bottom-2 left-2 right-2 transition-opacity duration-300 gradient-premium text-white hover:opacity-90 font-semibold shadow-lg opacity-0 md:group-hover:opacity-100"
        size="sm"
      >
        <Video className="w-4 h-4 mr-2" />
        Create Video
      </Button>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if actual data props change
  return (
    prevProps.imageUrl === nextProps.imageUrl &&
    prevProps.imageId === nextProps.imageId &&
    prevProps.index === nextProps.index &&
    prevProps.user?.id === nextProps.user?.id
    // Ignoring onToggle, onImageLoad, and onLoginRequired as they are stable callbacks
  );
});

GalleryImageItem.displayName = "GalleryImageItem";

export default GalleryImageItem;
